<template>
	<li
		v-for="(menuItem, menuIndex) in menuItems"
		:id="menuItem?.itemId"
		:key="menuIndex"
		v-tooltip="{
			content: menuItem.title,
			delay: { show: collapsed ? 500 : 999999, hide: 100 },
		}"
		:class="[
			!menuItem?.allowAnonymous && !isAuthenticated()
				? 'opacity-30'
				: 'opacity-100',
			menuItem?.className,
		]"
		class="min-w-10 cursor-pointer list-none select-none"
	>
		<div
			class="flex items-center justify-between rounded hover:bg-won-green-light"
			:class="
				(menuItem?.isActive && menuItem.isActive()) ||
					activeRoute === menuItem.name
					? 'won-light-green-background-color won-color-green'
					: 'won-color-nav-gray'
			"
		>
			<div
				class="relative flex w-full min-w-10! items-center justify-between p-2.5"
				@click="() => menuItemClickHandler(menuItem)"
			>
				<div class="flex items-center gap-2">
					<span v-html="menuItem.icon" />

					<span
						v-if="!collapsed"
						class="text-sm font-medium whitespace-nowrap"
						v-text="menuItem.title"
					/>

					<span
						v-if="menuItem.showNewLabel && collapsed"
						class="absolute top-1.5 right-1.5 size-2! rounded-full bg-won-red"
					/>
				</div>

				<NewLabel v-if="menuItem?.showNewLabel === true && !collapsed" />
			</div>

			<div
				v-if="menuItem?.items?.length > 0 && !collapsed"
				class="flex items-center pr-2.5"
				@click="() => updateVisibleSubMenusHandler(menuItem.name)"
			>
				<ChevronDownIcon
					class="size-4 text-won-gray-blue"
					:class="visibleSubMenu.has(menuItem.name) && '-rotate-180'"
				/>
			</div>
		</div>

		<ul
			v-if="menuItem?.items?.length && visibleSubMenu.has(menuItem.name)"
			class="mt-0.5 flex flex-col gap-0.5 pl-0!"
			:class="{ 'ml-3': !collapsed }"
		>
			<sidebar-menu
				:active-route="activeRoute"
				:menu-items="menuItem?.items"
			/>
		</ul>
	</li>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { setAuthState } from '../composables/auth';
import { useSidebarStore } from '../store/sidebar.store';

import NewLabel from './library/new-label.vue';

import { useAuthPopupStore } from '../store/auth-popup.store';

const props = defineProps({
	activeRoute: {
		type: String,
		required: true,
	},
	menuItems: {
		type: Array,
		default() {
			return [];
		},
	},
});

const router = useRouter();
const authPopupStore = useAuthPopupStore();
const sidebarStore = useSidebarStore();

const { isAuthenticated } = setAuthState();
const { collapsed } = storeToRefs(sidebarStore);
const visibleSubMenu = ref(new Set());

const navigateTo = (link) => {
	const { name, disabled, allowAnonymous } = link;

	if (!isAuthenticated() && allowAnonymous === false) {
		sidebarStore.collapseOnMobile();
		authPopupStore.setShowAuthPopup(name === 'resumes' ? 'resumes.edit' : null);
		return;
	}

	// A disabled item does nothing, so it leaves the drawer open; collapsing below the guards
	// keeps that true.
	if (disabled) {
		return;
	}

	sidebarStore.collapseOnMobile();
	router.push({ name });
};

const updateVisibleSubMenusHandler = (menu) => {
	visibleSubMenu.value.has(menu) ? visibleSubMenu.value.delete(menu) : visibleSubMenu.value.add(menu);
};

const externalLinkHandler = (url) => {
	sidebarStore.collapseOnMobile();

	const link = document.createElement('a');
	link.href = url;
	link.target = '_blank';

	document.body.appendChild(link);

	link.click();
	link.remove();
};

const menuItemClickHandler = (menuItem) => {
	if (menuItem?.isExternalRoute) {
		externalLinkHandler(menuItem.name);
	} else {
		navigateTo(menuItem);
	}
};

watch(() => props.activeRoute, (val) => {
	visibleSubMenu.value = ['resumes.edit', 'resume-tool'].includes(val)
		? new Set(['resumes'])
		: new Set();
}, { immediate: true });
</script>
